/**
 * modes/city-mode.js
 * Quản lý khởi tạo bối cảnh đường phố đô thị Việt Nam (Phố Phường VN) và phương tiện xe cộ.
 */

import * as THREE from 'three';
import { BlockyAvatar } from '../../avatars/blocky-avatar.js';
import { BlockyAnimal } from '../../avatars/blocky-animal.js';
import { disposeObject3D } from '../../core/dispose.js';
import { enableShadowCasting, enableVehicleShadowCasting } from '../../core/shadow-utils.js';

export function setupVietnamCityMode(isDrama = false, state, clearAvatarsFn) {
  if (typeof clearAvatarsFn === 'function') clearAvatarsFn(state);
  clearCityScene(state);

  if (state.studioEnvironmentGroup) state.studioEnvironmentGroup.visible = false;

  if (!isDrama && state.camera && state.controls) {
    state.camera.position.set(-6.5, 4.2, 11.5);
    state.controls.target.set(0, 1.5, 0);
  }

  const cityGroup = new THREE.Group();
  cityGroup.name = 'VietnamCity';

  // 1. Mặt đường nhựa, vạch kẻ, vạch sang đường, bó vỉa & vỉa hè lát gạch terrazzo
  const street = state.cityBuilder.createStreet({ length: 36, roadWidth: 7, sidewalkWidth: 5.25, crosswalkX: 7.5 });
  cityGroup.add(street);

  // 3. Dãy Nhà phố ống Việt Nam đa dạng kiến trúc bên lề đường
  const housesConfig = [
    { x: -12.5, style: 'classic', storeName: 'Phở Bò Gia Truyền', signColor: '#b91c1c', wallColor: '#fef08a' },
    { x: -7.8, style: 'cafe', storeName: 'Cà Phê Phố Cổ', signColor: '#1c1917', wallColor: '#facc15' },
    { x: -3.0, style: 'modern', storeName: 'Kiến Trúc & Nội Thất', signColor: '#1e3a8a', wallColor: '#f8fafc' },
    { x: 3.2, style: 'commercial', storeName: 'Nhà Thuốc 24h', signColor: '#0284c7', wallColor: '#ffffff' }
  ];

  housesConfig.forEach((cfg) => {
    const house = state.cityBuilder.createTubeHouse({
      style: cfg.style,
      storeName: cfg.storeName,
      signColor: cfg.signColor,
      wallColor: cfg.wallColor
    });
    house.position.set(cfg.x, 0.22, -8.5);
    // Tắt castShadow cho nhà ống: bóng đổ vào chính mặt tiền nhà, không ảnh hưởng lòng đường
    house.traverse((m) => {
      if (m.isMesh) m.castShadow = false;
    });
    cityGroup.add(house);
  });

  // Biệt thự hiện đại ở góc đường (tắt castShadow vì nằm ngoài mép sau vỉa hè)
  const villa = state.cityBuilder.createVilla();
  villa.position.set(11, 0.22, -8.5);
  villa.traverse((m) => {
    if (m.isMesh) m.castShadow = false;
  });
  cityGroup.add(villa);

  // Cột điện bê tông Việt Nam: chỉ cột bê tông và xà lớn đổ bóng, dây điện nhỏ bỏ qua
  const pole1 = state.cityBuilder.createPowerPole();
  pole1.position.set(-6, 0.22, -4.5);
  enableShadowCasting(pole1, { minRadius: 0.15 });
  cityGroup.add(pole1);

  const pole2 = state.cityBuilder.createPowerPole();
  pole2.position.set(5.5, 0.22, -4.5);
  enableShadowCasting(pole2, { minRadius: 0.15 });
  cityGroup.add(pole2);

  // 4. Cây xanh đa dạng chủng loại đường phố Việt Nam: chỉ thân và tán chính đổ bóng
  const treeConfigs = [
    { kind: 'hoa_sua', x: -16.2, z: -4.6 },
    { kind: 'phuong', x: -10, z: -4.5 },
    { kind: 'bang', x: isDrama ? -2.8 : 0, z: -4.5 },
    { kind: 'dua', x: 8.5, z: -4.5 },
    { kind: 'bang_lang', x: 15.8, z: -4.6 },
    { kind: 'chuoi', x: -16, z: 7.6 },
    { kind: 'khom_tre', x: 16.2, z: 7.9 },
    { kind: 'chau_quat', x: -4.9, z: -5.2 },
    { kind: 'chau_hoa_giay', x: 1.6, z: -5.25 },
    { kind: 'chau_cau', x: 5.0, z: -5.3 }
  ];
  const shadowTreeKinds = new Set(['hoa_sua', 'phuong', 'bang', 'dua', 'bang_lang']);
  treeConfigs.forEach((cfg) => {
    const tree = state.cityBuilder.createTree(cfg.kind);
    tree.position.set(cfg.x, 0.22, cfg.z);
    if (shadowTreeKinds.has(cfg.kind)) {
      enableShadowCasting(tree, { minRadius: 0.38 });
    } else {
      tree.traverse((m) => {
        if (m.isMesh) m.castShadow = false;
      });
    }
    cityGroup.add(tree);
  });

  // 5. Phương tiện: đi đúng làn bên phải (chỉ thân xe và bánh xe đổ bóng)
  state.movingVehicles = [];
  const vehicleConfigs = [
    { obj: state.cityBuilder.createVinFastCar(true), x: -8, z: 1.5, speed: 4.5, dir: 1 },
    { obj: state.cityBuilder.createMotorbike('#1d4ed8', { plate: '29-B1\n268.86' }), x: 3, z: 2.8, speed: 6.0, dir: 1 },
    { obj: state.cityBuilder.createVinFastCar(false, { color: '#f1f5f9' }), x: 12, z: -1.5, speed: 3.8, dir: -1 },
    { obj: state.cityBuilder.createMotorbike('#dc2626'), x: 6, z: -2.8, speed: 5.5, dir: -1 }
  ];
  vehicleConfigs.forEach((cfg) => {
    cfg.obj.position.set(cfg.x, 0, cfg.z);
    if (cfg.dir < 0) cfg.obj.rotation.y = Math.PI;
    const isBike = cfg.obj.name.toLowerCase().includes('motorbike');
    enableVehicleShadowCasting(cfg.obj, { isMotorbike: isBike });
    cityGroup.add(cfg.obj);
    state.movingVehicles.push({ obj: cfg.obj, speed: cfg.speed, dir: cfg.dir, minX: -18, maxX: 18 });
  });

  // Xe máy đỗ trên vỉa hè trước quán phở (chỉ thân và bánh xe đổ bóng)
  [
    [-13.2, '#dc2626'],
    [-12.3, '#f8fafc'],
    [-11.4, '#1d4ed8']
  ].forEach(([px, color]) => {
    const parked = state.cityBuilder.createMotorbike(color, { parked: true });
    parked.position.set(px, 0.22, -4.6);
    parked.rotation.y = Math.PI / 2;
    enableVehicleShadowCasting(parked, { isMotorbike: true });
    cityGroup.add(parked);
  });

  // 6. Thú cưng đường phố Việt Nam (chó quán phở, mèo cà phê, đàn gà bới thóc)
  const streetPets = [];

  const dog = new BlockyAnimal('DOG', 'shiba');
  dog.root.name = 'Animal_Dog';
  dog.root.position.set(-14.4, 0.22, -4.3);
  dog.root.rotation.y = Math.PI * 0.15;
  dog.playAction('sit');
  cityGroup.add(dog.root);
  streetPets.push(dog);

  const cat = new BlockyAnimal('CAT', 'calico');
  cat.root.name = 'Animal_Cat';
  // Mèo ngồi vỉa hè trước nhà hiện đại: bậc thềm quán cà phê chỉ sâu 0.4 m, không đủ chỗ cho mèo (dài ~0.84 m kể cả đuôi)
  // mà không lấn vào tường / cánh cửa gấp. Vị trí này tránh chậu quất (x -4.9) và bồn cây bàng khi diễn kịch bản (x -2.8)
  cat.root.position.set(-4.0, 0.22, -5.1);
  cat.root.rotation.y = 0.4;
  cat.playAction('sit');
  cityGroup.add(cat.root);
  streetPets.push(cat);

  const chickenCoords = [
    { x: -8.0, z: 6.2, name: 'Animal_Chicken_1' },
    { x: -9.5, z: 6.6, name: 'Animal_Chicken_2' },
    { x: -7.0, z: 5.9, name: 'Animal_Chicken_3' }
  ];
  chickenCoords.forEach((coord) => {
    const chicken = new BlockyAnimal('CHICKEN');
    chicken.root.name = coord.name;
    chicken.root.position.set(coord.x, 0.22, coord.z);
    chicken.playAction('peck');
    cityGroup.add(chicken.root);
    streetPets.push(chicken);
  });

  // Kiểm tra va chạm Box3 giữa thú cưng đường phố và các vật thể tĩnh (bồn cây Tree, tường nhà TubeHouse)
  const petBox = new THREE.Box3();
  cityGroup.updateMatrixWorld(true);
  cityGroup.traverse((child) => {
    if (child.name && child.name.startsWith('Tree')) {
      const pitBox = new THREE.Box3(
        new THREE.Vector3(child.position.x - 0.7, 0.22, child.position.z - 0.7),
        new THREE.Vector3(child.position.x + 0.7, 0.45, child.position.z + 0.7)
      );
      [dog, cat].forEach((pet) => {
        petBox.setFromObject(pet.root);
        if (petBox.intersectsBox(pitBox)) {
          console.warn(`[city-mode] Cảnh báo va chạm: ${pet.root.name} nằm trong bồn cây ${child.name}`);
        }
      });
    } else if (child.name && child.name.startsWith('TubeHouse')) {
      // Khối thân nhà luôn là mesh đầu tiên của nhóm TubeHouse_* (mái bạt, bàn ghế vỉa hè không tính)
      const bodyMesh = child.children.find((c) => c.isMesh);
      if (!bodyMesh) return;
      const houseBodyBox = new THREE.Box3().setFromObject(bodyMesh);
      [dog, cat].forEach((pet) => {
        petBox.setFromObject(pet.root);
        if (petBox.intersectsBox(houseBodyBox)) {
          console.warn(`[city-mode] Cảnh báo va chạm: ${pet.root.name} đâm xuyên tường nhà ${child.name}`);
        }
      });
    }
  });

  streetPets.forEach((pet) => {
    enableShadowCasting(pet.root, { minRadius: 0.12 });
    state.activeAvatars.push(pet);
  });
  state.cityAnimals = streetPets;

  state.scene.add(cityGroup);
  state.cityGroup = cityGroup;

  // 7. Cư dân Việt Nam đi dạo trên vỉa hè (chỉ hiển thị khi KHÔNG phải kịch bản)
  if (!isDrama) {
    const aoDaiLady = new BlockyAvatar('CO_GAI_AO_DAI');
    aoDaiLady.root.position.set(-3.5, 0.22, 5.0);
    aoDaiLady.playAction('walk');
    state.scene.add(aoDaiLady.root);
    state.activeAvatars.push(aoDaiLady);

    const grabDriver = new BlockyAvatar('TAI_XE_GRAB');
    grabDriver.root.position.set(2.0, 0.22, 5.0);
    grabDriver.root.rotation.y = Math.PI;
    grabDriver.playAction('walk');
    state.scene.add(grabDriver.root);
    state.activeAvatars.push(grabDriver);

    const bacBa = new BlockyAvatar('BAC_BA_BA');
    bacBa.root.position.set(-7.5, 0.22, -4.6);
    bacBa.root.rotation.y = 0.2;
    bacBa.playAction('wave');
    state.scene.add(bacBa.root);
    state.activeAvatars.push(bacBa);

    const beMinh = new BlockyAvatar('BE_MINH');
    beMinh.root.position.set(6.5, 0.22, 5.0);
    beMinh.playAction('walk');
    state.scene.add(beMinh.root);
    state.activeAvatars.push(beMinh);

    [aoDaiLady, grabDriver, bacBa, beMinh].forEach((resident) => {
      enableShadowCasting(resident.root, { minRadius: 0.15 });
    });

    state.mainAvatar = aoDaiLady;
  }
}

export function clearCityScene(state) {
  const audioModal = document.getElementById('drama-audio-modal');
  const audioBackdrop = document.getElementById('drama-audio-backdrop');
  if (audioModal) audioModal.style.display = 'none';
  if (audioBackdrop) audioBackdrop.style.display = 'none';

  if (state.cityAnimals) {
    state.cityAnimals.forEach((animal) => {
      const idx = state.activeAvatars?.indexOf(animal);
      if (idx !== -1 && idx !== undefined) {
        state.activeAvatars.splice(idx, 1);
      }
      animal.dispose();
    });
    state.cityAnimals = [];
  }

  if (state.cityGroup) {
    disposeObject3D(state.cityGroup, { disposeTextures: true });
    state.scene.remove(state.cityGroup);
    state.cityGroup = null;
    state.cityBuilder.dispose();
  }
  state.movingVehicles = [];
}
